import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";
import { LegalAcceptForm } from "@/components/legal/LegalAcceptForm";

/** Renders children unless the signed-in actor must (re)accept agreements while enforcement is on. */
export default function LegalGate({ actor, children }) {
  const [status, setStatus] = useState(null);
  const [justAccepted, setJustAccepted] = useState(false);
  const successRef = useRef(null);
  useEffect(() => {
    if (justAccepted) successRef.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [justAccepted]);

  const load = useCallback(() => api.get("/legal/status").then((r) => setStatus(r.data)).catch(() => setStatus({ complete: true })), []);
  useEffect(() => { load(); }, [load]);

  if (!status) return <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  if (justAccepted) {
    return (
      <div ref={successRef} className="max-w-xl mx-auto py-16 text-center" data-testid="legal-accept-success">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
        <h2 className="font-display text-2xl text-slate-900 mt-3">Terms &amp; Conditions accepted.</h2>
        <p className="text-sm text-slate-600 mt-2">
          A copy is available anytime under Account → {actor === "MERCHANT" ? "Terms & Agreements" : "Terms & Privacy"}.
        </p>
        <button onClick={() => setJustAccepted(false)} data-testid="legal-accept-continue"
                className="pf-btn-primary px-6 py-2.5 rounded-full text-sm font-semibold mt-6">Continue</button>
      </div>
    );
  }

  const blocked = status.enforcement_active && status.actor_type === actor && !status.complete;
  if (!blocked) return children;
  return (
    <LegalAcceptForm actor={actor} missing={status.missing} current={status.current}
                     onAccepted={async () => { await load(); setJustAccepted(true); }} />
  );
}
